import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { CreateChancel, Input, SelectAutocompleteMultiple } from 'components';
import { domainActions, goalActions, httpRequestsOnErrorsActions, httpRequestsOnSuccessActions } from 'store';
import { FindLoad, useModal, FindSuccessItem, goalDataTypes, FindErrorItem, goalPerRateEnumList, goalDataTypeEnums, Images } from 'utils';
import { goalTypes, TAModes, teachingMethodEnums, teachingMethods } from './constants';

export const AddGoal = ({ info, isClone, requestParams, afterSuccess }) => {
    const client = useSelector((state) => state.client.clientItemInfo);
    const domainList = useSelector((state) => state.domains.domainList);
    const ACTION_TYPE = isClone ? 'CLONE_GOAL' : info ? 'EDIT_GOAL' : 'CREATE_GOAL';
    const dispatch = useDispatch();
    const success = FindSuccessItem(ACTION_TYPE);
    const backError = FindErrorItem(ACTION_TYPE);
    const loader = FindLoad(ACTION_TYPE);
    const { close } = useModal();
    const {
        handleSubmit,
        control,
        clearErrors,
        setError,
        setValue,
        watch,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            minTrials: 1,
        },
    });
    const dataTypeLocked = info?.id && !isClone;
    const watchedDataType = watch('dataType');
    const dataTypeOptions =
        isClone && info?.dataType !== goalDataTypeEnums.RATE
            ? goalDataTypes.filter((option) => option.id !== goalDataTypeEnums.RATE)
            : goalDataTypes;

    useEffect(() => {
        dispatch(domainActions.getDomains());
    }, []);

    useEffect(() => {
        if (info) {
            const params = {
                ...info,
                description: info?.description || '',
                maxTrials: info?.maxTrials ?? '',
            };

            info?.domain ? (params.domain = info?.domain?.id) : delete params.domain;
            reset(params);
        }
    }, [info]);

    useEffect(() => {
        if (success) {
            dispatch(httpRequestsOnSuccessActions.removeSuccess(ACTION_TYPE));
            close();
            if (success?.data && afterSuccess) {
                afterSuccess(success?.data);
            }
        }
        if (backError?.error === 'Goal conflict: coincident record found') {
            dispatch(httpRequestsOnErrorsActions.removeError(ACTION_TYPE));
            setError('name', {
                message: 'Goal name must be unique',
            });
        }
        // The name belongs to a deleted goal — the API says to restore that one instead.
        if (typeof backError?.error === 'string' && backError.error.startsWith('A deleted goal is already named')) {
            dispatch(httpRequestsOnErrorsActions.removeError(ACTION_TYPE));
            setError('name', { message: 'A deleted goal has this name — restore it from "Show deleted" instead' });
        }
    }, [success, backError]);

    const handleCreate = (data) => {
        const params = {
            clientId: client?.id,
            ...data,
            description: data.description,
        };

        data?.minTrials ? (params.minTrials = +data.minTrials) : delete params.minTrials;
        // Empty max = no limit: omitted on create, `null` on edit so an existing limit is removed.
        const hasMax = data?.maxTrials !== '' && data?.maxTrials != null;
        if (hasMax) params.maxTrials = +data.maxTrials;
        else if (info && !isClone) params.maxTrials = null;
        else delete params.maxTrials;
        if (data?.dataType !== goalDataTypeEnums.RATE || !data?.timeUnit) {
            delete params.timeUnit;
        }
        if (isClone) {
            const clonePayload = { };
            if(data?.dataType !== info?.dataType){
                clonePayload.dataType = data?.dataType;
            }
            if(data?.name !== info?.name){
                clonePayload.name = data?.name;
            }
            dispatch(goalActions.cloneGoal(info.id, clonePayload, requestParams));
        } else if (info) {
            dispatch(goalActions.editGoal(info.id, params, requestParams));
        } else {
            dispatch(goalActions.createGoal(params, requestParams));
        }
    };

    return (
        <div style={{ width: '1000px', maxWidth: '100%' }}>
            <p className="modal-header-title">{isClone ? 'Duplicate Goal' : info ? 'Edit Goal' : 'Add Goal'}</p>
            <form onSubmit={handleSubmit(handleCreate)}>
                <div className="modal-body-wrapper">
                    <div className="flex-align-start" style={{ gap: 16 }}>
                        <div className="full-width">
                            <Input
                                name="name"
                                label="Goal Name*"
                                variant="outlined"
                                control={control}
                                rules={{
                                    required: 'Goal name is required.',
                                    validate: (value) => value.trim().length > 0 || 'Goal name cannot be empty spaces.',
                                }}
                            />
                            <SelectAutocompleteMultiple
                                name={`teachingMethod`}
                                label={'Teaching Method'}
                                control={control}
                                options={teachingMethods}
                                renderValue={(option) => option?.label}
                            />
                            <SelectAutocompleteMultiple
                                loadType={'GET_DOMAINS'}
                                name={`domain`}
                                label={'Domain'}
                                control={control}
                                options={domainList}
                                renderValue={(option) => option?.name}
                            />
                            {watch('teachingMethod') === teachingMethodEnums.TASK_ANALYSIS && (
                                <SelectAutocompleteMultiple
                                    name={`TAMode`}
                                    label={'Chaining*'}
                                    control={control}
                                    options={TAModes}
                                    rules={{ required: 'Mode is required.' }}
                                    renderValue={(option) => option?.label}
                                />
                            )}
                        </div>

                        <div className="full-width">
                            <SelectAutocompleteMultiple
                                name={`type`}
                                label={'Type*'}
                                control={control}
                                options={goalTypes}
                                rules={{ required: 'Type is required.' }}
                                renderValue={(option) => option?.label}
                            />

                            <div className="flex-align-center" style={{ gap: 12 }}>
                                <SelectAutocompleteMultiple
                                    name={`dataType`}
                                    label={'Data Collection Method*'}
                                    control={control}
                                    options={dataTypeOptions}
                                    rules={{ required: 'Mode is required.' }}
                                    renderValue={(option) => option?.label}
                                    disabled={dataTypeLocked}
                                    handleChange={() => setValue('timeUnit', null)}
                                />
                                {(dataTypeLocked ? info?.dataType === goalDataTypeEnums.RATE : watchedDataType === goalDataTypeEnums.RATE) && (
                                    <SelectAutocompleteMultiple
                                        name={'timeUnit'}
                                        label={'Rate Measured Per*'}
                                        control={control}
                                        options={goalPerRateEnumList}
                                        rules={{ required: 'Rate Measured is required.' }}
                                        renderValue={(option) => option?.label}
                                        disabled={isClone}
                                    />
                                )}
                            </div>


                            <div className="flex-align-start" style={{ gap: 12 }}>
                                <Input
                                    type="number"
                                    name="minTrials"
                                    label="Min Trials*"
                                    variant="outlined"
                                    control={control}
                                    rules={{
                                        required: 'Min trials is required.',
                                        min: {
                                            value: 0,
                                            message: 'Min trials cannot be negative.',
                                        },
                                    }}
                                    min={0}
                                />
                                {/* Hard cap per session — empty means no limit. */}
                                <Input
                                    type="number"
                                    name="maxTrials"
                                    label="Max Trials"
                                    variant="outlined"
                                    control={control}
                                    rules={{
                                        validate: (value) => {
                                            if (value === '' || value === null || value === undefined) return true;
                                            if (!Number.isInteger(+value) || +value < 1) return 'Max trials must be a whole number of at least 1.';
                                            const min = watch('minTrials');
                                            if (min !== '' && min != null && +value < +min) return 'Max trials cannot be less than min trials.';
                                            return true;
                                        },
                                    }}
                                    min={1}
                                />
                            </div>
                        </div>
                    </div>

                    {isClone && (
                        <div className="clone-goal-info-box">
                            <img src={Images.infoCircle} alt="info" />
                            <span>
                                All goal settings and{' '}
                                <b>
                                    {info?.targets?.length ?? 0} {(info?.targets?.length ?? 0) === 1 ? 'target' : 'targets'}
                                </b>{' '}
                                from the original goal will be copied to the new goal.
                            </span>
                        </div>
                    )}
                    {isClone && watchedDataType !== info?.dataType && (
                        <div className='changing-goal-method-box'>
                            <img src={Images.alertWarning} alt='warning'/>
                            <span>Changing this method will reset related options.</span>
                        </div>
                    )}
                </div>
                <CreateChancel
                    type="submit"
                    loader={!!loader.length}
                    create={isClone ? 'Duplicate' : info ? 'Save' : 'Add'}
                    chancel={'Cancel'}
                    onClose={close}
                    buttonWidth="100%"
                    cancelRight={'24px'}
                />
            </form>
        </div>
    );
};
